/**
 * Shabbat Candles Animation Module
 *
 * Renders two flanking Shabbat candles with organic flickering flames.
 * Features:
 * - Two candles flanking the Torah scroll
 * - Organic flame shapes using bezier curves
 * - Random flicker using Perlin-style noise
 * - Radial gradient glow emanating from flames
 * - Warm golden/orange color palette
 */

import { LOADER_COLORS, type AnimationModule, type AnimationPhase, type Particle } from '../types';
import { Easing, fbmNoise, lerp } from './AnimationEngine';
import { random, randomRange } from '@/utils/random';

interface Candle {
  x: number;
  baseY: number;
  width: number;
  height: number;
  flamePhase: number;
  flameHeight: number;
  flameWidth: number;
  glowIntensity: number;
  flickerSeed: number;
}

interface FlameState {
  intensity: number;
  sway: number;
  heightMod: number;
}

export class ShabbatCandlesAnimation implements AnimationModule {
  id = 'shabbat-candles';

  // Candles
  private candles: Candle[] = [];
  private flameStates: FlameState[] = [];

  // Animation timing
  private time = 0;
  private introProgress = 0;
  private exitProgress = 0;
  private isExiting = false;

  // Exit particles (flames burst into particles)
  private exitParticles: Particle[] = [];

  init(_ctx: CanvasRenderingContext2D, width: number, height: number): void {
    this.resize(width, height);
  }

  resize(width: number, height: number): void {
    // Calculate candle dimensions based on canvas size
    // Increased 25% for better mobile visibility
    const minDimension = Math.min(width, height);
    const scale = 0.75; // 25% larger than previous 0.6
    const candleWidth = minDimension * 0.022 * scale;
    const candleHeight = minDimension * 0.16 * scale;

    // Calculate scroll width to position candles outside it
    const scrollWidth = minDimension * 0.5 * scale;
    const handleWidth = scrollWidth * 0.14;
    // Position candles OUTSIDE the scroll handles
    const spacing = (scrollWidth * 0.7) / 2 + handleWidth + candleWidth * 1.5;

    const centerX = width / 2;
    const centerY = height / 2 + 5;

    // Position candles on either side of the scroll, outside the handles
    this.candles = [
      {
        x: centerX - spacing,
        baseY: centerY + candleHeight * 0.3,
        width: candleWidth,
        height: candleHeight,
        flamePhase: 0,
        flameHeight: candleHeight * 0.45,
        flameWidth: candleWidth * 1.8,
        glowIntensity: 1, // Start visible
        flickerSeed: random() * 1000,
      },
      {
        x: centerX + spacing,
        baseY: centerY + candleHeight * 0.3,
        width: candleWidth,
        height: candleHeight,
        flamePhase: Math.PI * 0.5,
        flameHeight: candleHeight * 0.45,
        flameWidth: candleWidth * 1.8,
        glowIntensity: 1,
        flickerSeed: random() * 1000 + 500,
      },
    ];

    // Initialize flame states
    this.flameStates = this.candles.map(() => ({
      intensity: 0,
      sway: 0,
      heightMod: 1,
    }));
  }

  update(deltaTime: number, phase: AnimationPhase, progress: number): void {
    const dt = deltaTime / 1000;
    this.time += dt;

    switch (phase) {
      case 'intro':
        this.introProgress = progress;
        // Candles and flames visible from start, just grow slightly
        this.candles.forEach((candle, i) => {
          // Candles visible from the beginning
          candle.glowIntensity = 0.7 + Easing.easeOutCubic(progress) * 0.3;
          // Flames grow during intro
          this.flameStates[i].intensity = 0.6 + Easing.easeOutQuad(progress) * 0.4;
        });
        break;

      case 'loop':
        // Full flame animation - candles always visible
        this.candles.forEach((candle, i) => {
          candle.glowIntensity = 1;
          this.flameStates[i].intensity = 1;
        });
        break;

      case 'exit':
        this.exitProgress = progress;
        if (!this.isExiting) {
          this.isExiting = true;
          this.createExitParticles();
        }
        // Flames diminish and burst into particles
        this.candles.forEach((candle, i) => {
          candle.glowIntensity = lerp(1, 0, Easing.easeInQuad(progress));
          this.flameStates[i].intensity = lerp(1, 0, Easing.easeInCubic(progress * 0.7));
        });
        this.updateExitParticles(dt);
        break;

      case 'complete':
        // Nothing visible
        break;
    }

    // Update flame animation
    this.updateFlames(dt);
  }

  private updateFlames(dt: number): void {
    this.candles.forEach((candle, i) => {
      const state = this.flameStates[i];

      // Organic flicker using noise
      const flicker = fbmNoise(this.time * 3 + candle.flickerSeed, 3, candle.flickerSeed);
      state.sway = (flicker - 0.5) * 0.3;
      state.heightMod = 0.9 + flicker * 0.2;

      // Update flame phase for internal animation
      candle.flamePhase += dt * 4;
    });
  }

  private createExitParticles(): void {
    this.exitParticles = [];

    this.candles.forEach((candle) => {
      const flameTop = candle.baseY - candle.height - candle.flameHeight;

      // Create burst of particles from flame
      for (let i = 0; i < 20; i++) {
        const angle = random() * Math.PI * 2;
        const speed = randomRange(50, 150);

        this.exitParticles.push({
          x: candle.x + randomRange(-0.5, 0.5) * candle.flameWidth,
          y: flameTop + random() * candle.flameHeight * 0.5,
          vx: Math.cos(angle) * speed * 0.5,
          vy: -Math.abs(Math.sin(angle)) * speed - 30,
          life: 1,
          maxLife: 1,
          size: randomRange(2, 6),
          color: random() > 0.5 ? LOADER_COLORS.flameMiddle : LOADER_COLORS.gold,
          alpha: 1,
        });
      }
    });
  }

  private updateExitParticles(dt: number): void {
    this.exitParticles.forEach((particle) => {
      particle.x += particle.vx * dt;
      particle.y += particle.vy * dt;
      particle.vy += 50 * dt; // Slight gravity, but still floating up mostly
      particle.life -= dt * 1.5;
      particle.alpha = Math.max(0, particle.life / particle.maxLife);
      particle.size *= 0.98;
    });

    // Remove dead particles
    this.exitParticles = this.exitParticles.filter((p) => p.life > 0);
  }

  render(ctx: CanvasRenderingContext2D): void {
    // Render each candle - always render if we have candles
    this.candles.forEach((candle, i) => {
      const state = this.flameStates[i];

      // Always render candles during intro and loop phases
      this.renderGlow(ctx, candle, state);
      this.renderCandle(ctx, candle);
      this.renderFlame(ctx, candle, state);
    });

    // Render exit particles
    this.renderExitParticles(ctx);
  }

  private renderGlow(ctx: CanvasRenderingContext2D, candle: Candle, _state: FlameState): void {
    if (candle.glowIntensity <= 0) return;

    const flameTop = candle.baseY - candle.height - candle.flameHeight * 0.5;
    const glowRadius = candle.flameWidth * 4 * candle.glowIntensity;

    ctx.save();

    // Outer glow
    const gradient = ctx.createRadialGradient(
      candle.x,
      flameTop,
      0,
      candle.x,
      flameTop,
      glowRadius
    );
    gradient.addColorStop(0, `rgba(255, 200, 100, ${0.3 * candle.glowIntensity})`);
    gradient.addColorStop(0.4, `rgba(255, 180, 80, ${0.15 * candle.glowIntensity})`);
    gradient.addColorStop(1, 'rgba(255, 180, 80, 0)');

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(candle.x, flameTop, glowRadius, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  private renderCandle(ctx: CanvasRenderingContext2D, candle: Candle): void {
    const { x, baseY, width, height } = candle;
    // Always show candles, with gentle scale animation
    const introScale = 0.8 + Easing.easeOutQuad(Math.min(this.introProgress * 1.2, 1)) * 0.2;
    const exitScale = this.isExiting ? lerp(1, 0.6, Easing.easeInQuad(this.exitProgress)) : 1;
    const scale = introScale * exitScale;

    if (scale <= 0.1) return;

    ctx.save();
    ctx.translate(x, baseY);
    ctx.scale(scale, scale);

    const candleTop = -height;

    // Candle body gradient
    const bodyGradient = ctx.createLinearGradient(-width / 2, 0, width / 2, 0);
    bodyGradient.addColorStop(0, '#f5f5f0');
    bodyGradient.addColorStop(0.3, '#ffffff');
    bodyGradient.addColorStop(0.7, '#f0f0eb');
    bodyGradient.addColorStop(1, '#e8e8e3');

    ctx.fillStyle = bodyGradient;
    ctx.beginPath();
    ctx.roundRect(-width / 2, candleTop, width, height, [2, 2, 0, 0]);
    ctx.fill();

    // Subtle candle outline
    ctx.strokeStyle = 'rgba(200, 200, 195, 0.5)';
    ctx.lineWidth = 0.5;
    ctx.stroke();

    // Wick
    const wickHeight = height * 0.08;
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 1.5;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(0, candleTop);
    ctx.lineTo(0, candleTop - wickHeight);
    ctx.stroke();

    // Dripping wax effect
    this.renderWaxDrips(ctx, width, height);

    ctx.restore();
  }

  private renderWaxDrips(ctx: CanvasRenderingContext2D, width: number, height: number): void {
    ctx.fillStyle = 'rgba(255, 255, 250, 0.8)';

    // A few wax drips
    const drips = [
      { x: -width * 0.3, length: height * 0.15 },
      { x: width * 0.25, length: height * 0.1 },
    ];

    drips.forEach((drip) => {
      ctx.beginPath();
      ctx.ellipse(drip.x, -height + drip.length, width * 0.1, drip.length, 0, 0, Math.PI * 2);
      ctx.fill();
    });
  }

  private renderFlame(ctx: CanvasRenderingContext2D, candle: Candle, state: FlameState): void {
    if (state.intensity <= 0) return;

    const { x, baseY, height, flameHeight, flameWidth } = candle;
    const flameBottom = baseY - height - height * 0.03;
    const actualFlameHeight = flameHeight * state.heightMod * state.intensity;
    const sway = state.sway * flameWidth;

    ctx.save();

    // Flame is made of multiple layers with different colors and shapes
    this.renderFlameLayer(
      ctx,
      x,
      flameBottom,
      flameWidth * 0.9,
      actualFlameHeight * 1.1,
      sway,
      LOADER_COLORS.flameOuter,
      0.6 * state.intensity,
      candle.flamePhase
    );
    this.renderFlameLayer(
      ctx,
      x,
      flameBottom,
      flameWidth * 0.65,
      actualFlameHeight * 0.9,
      sway * 0.8,
      LOADER_COLORS.flameMiddle,
      0.8 * state.intensity,
      candle.flamePhase + 0.5
    );
    this.renderFlameLayer(
      ctx,
      x,
      flameBottom,
      flameWidth * 0.35,
      actualFlameHeight * 0.6,
      sway * 0.5,
      LOADER_COLORS.flameInner,
      1 * state.intensity,
      candle.flamePhase + 1
    );

    ctx.restore();
  }

  private renderFlameLayer(
    ctx: CanvasRenderingContext2D,
    x: number,
    bottom: number,
    width: number,
    height: number,
    sway: number,
    color: string,
    alpha: number,
    phase: number
  ): void {
    ctx.save();
    ctx.globalAlpha = alpha;

    // Organic flame shape using bezier curves
    const tipX = x + sway;
    const tipY = bottom - height;
    const midY = bottom - height * 0.5;

    // Add subtle movement to control points
    const wiggle = Math.sin(phase) * width * 0.1;

    ctx.beginPath();
    ctx.moveTo(x, bottom);

    // Left side of flame
    ctx.bezierCurveTo(
      x - width * 0.5 + wiggle,
      bottom - height * 0.2,
      x - width * 0.4 - wiggle,
      midY,
      tipX,
      tipY
    );

    // Right side of flame
    ctx.bezierCurveTo(
      x + width * 0.4 + wiggle,
      midY,
      x + width * 0.5 - wiggle,
      bottom - height * 0.2,
      x,
      bottom
    );

    ctx.closePath();

    // Gradient fill
    const gradient = ctx.createLinearGradient(x, bottom, x, tipY);
    gradient.addColorStop(0, color);
    gradient.addColorStop(1, this.adjustColorAlpha(color, 0.3));

    ctx.fillStyle = gradient;
    ctx.fill();

    ctx.restore();
  }

  private adjustColorAlpha(color: string, alpha: number): string {
    // Simple hex to rgba conversion
    if (color.startsWith('#')) {
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    return color;
  }

  private renderExitParticles(ctx: CanvasRenderingContext2D): void {
    this.exitParticles.forEach((particle) => {
      ctx.save();
      ctx.globalAlpha = particle.alpha;

      const gradient = ctx.createRadialGradient(
        particle.x,
        particle.y,
        0,
        particle.x,
        particle.y,
        particle.size
      );
      gradient.addColorStop(0, particle.color);
      gradient.addColorStop(1, this.adjustColorAlpha(particle.color, 0));

      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
      ctx.fill();

      ctx.restore();
    });
  }

  triggerExit(): void {
    this.isExiting = true;
  }

  destroy(): void {
    this.exitParticles = [];
  }
}
